import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { templateHasConclusionsFields } from "@utils/reportTemplateSections";
import { APP_ENV, ENV_NAMES } from "@utils/utils";
import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Dialog, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportSectionsAiGeneratedStatusChoices,
  ReportPageLocationEnums,
  useCreateReportTemplateSectionsMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { EvaluationSubRoute, getNextRoute } from "@pages/EvaluationDetails/EvaluationDetails";
import { ReportNode, useReport } from "@providers/ReportProvider";

interface ConfirmGenerationContentProps {
  type: ReportPageLocationEnums;
  promptDialogOpen: boolean;
  setPromptDialogOpen: (bool: boolean) => void;
  onboardingData?: string;
  skipNavigate?: boolean;
}

const GenerationTypeLabelMapping: { [key in ReportPageLocationEnums]: string } = {
  [ReportPageLocationEnums.ReportPart_1]: "Report Part 1",
  [ReportPageLocationEnums.ReportPart_2]: "Report Part 2",
  [ReportPageLocationEnums.Conclusions]: "Conclusions",
  [ReportPageLocationEnums.Recommendations]: "Recommendations",
  [ReportPageLocationEnums.Appendix]: "Appendix",
  [ReportPageLocationEnums.TreatmentPlan]: "Treatment Plan",
};

const checkIsPredictionGenerating = (
  page: ReportPageLocationEnums,
  evaluationReport: ReportNode | undefined | null,
): boolean => {
  if (!evaluationReport) return false;
  return evaluationReport?.reportSections?.edges
    .map((edge) => edge?.node)
    .filter((section) => section?.pageLocation === page)
    .some(
      (section) => section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending,
    );
};

const checkIsPredictionGeneratedAlready = (
  page: ReportPageLocationEnums,
  evaluationReport: ReportNode | undefined | null,
): boolean => {
  if (!evaluationReport) return false;
  return evaluationReport.reportSections.edges
    .map((edge) => edge?.node)
    .filter((section) => section?.pageLocation === page)
    .some(
      (section) => section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted,
    );
};

const getPrimaryButtonText = (params: {
  isPending: boolean;
  showNotice: boolean;
  isPredictionGenerating: boolean;
}): string => {
  if (params.isPending) return "Generating...";
  if (params.showNotice) return "Yes, Continue";
  if (params.isPredictionGenerating) return "Okay";
  return "Generate";
};

// Extracted so the main dialog's cognitive complexity stays under the repo's SonarCloud
// threshold -- this three-way branch was pushing ConfirmGenerationDialog over it.
const ConfirmGenerationDialogBody: React.FC<{
  showNotice: boolean;
  isPredictionGenerating: boolean;
  generationTypeLabel: string;
}> = ({ showNotice, isPredictionGenerating, generationTypeLabel }) => {
  if (showNotice) {
    return (
      <>
        <Paragraph displayType="normal" colorType="secondary">
          You are about to resubmit a previous step. This will cause the upcoming pages of your assessment report to be
          regenerated. Any changes you previously made to these pages will be overwritten.
        </Paragraph>
        <Paragraph displayType="normal" colorType="secondary" className="my-4">
          Would you like to proceed?
        </Paragraph>
        <div className="p-4 bg-red-50">
          <Paragraph displayType="loud" className="inline text-red-500!">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 inline mr-2" />
            Warning:{" "}
          </Paragraph>
          <Paragraph displayType="normal" colorType="secondary" className="inline text-red-500!">
            &nbsp; This action is not reversible
          </Paragraph>
        </div>
      </>
    );
  }

  if (isPredictionGenerating) {
    return (
      <Paragraph displayType="normal" colorType="secondary">
        Content for another step is currently being generated. This may take a few moments. Please wait until the
        process is complete before proceeding.
      </Paragraph>
    );
  }

  return (
    <Small>
      By clicking "Generate", you will be kicking off the {generationTypeLabel} generation step. This step will take
      several minutes. While {generationTypeLabel} generation is happening, you will not be able to edit your report.
    </Small>
  );
};

export const ConfirmGenerationDialog: React.FC<ConfirmGenerationContentProps> = ({
  type,
  promptDialogOpen,
  setPromptDialogOpen,
  skipNavigate = false,
}) => {
  const { pathname } = useLocation();
  const go = useNavigate();

  const { evaluationId: reportId } = useParams();
  const { report, reportQuery } = useReport();
  // Not an org check: Kuwait's template happens to define no Conclusions fields, so this
  // evaluates the same as the old `isKuwaitUser` check for every org that exists today, but
  // is driven by the template rather than the org name.
  //
  // Deliberately NOT scoped to `type === Conclusions`: the old `isKuwaitUser` gate suppressed
  // confirmation for every generation `type` on this dialog, not just Conclusions, and PROA-1659
  // AC #2 requires Kuwait's stepper behavior to keep matching exactly. Narrowing this to
  // Conclusions-only would reintroduce confirmation dialogs for Report Part 1/2/Recommendations
  // that Kuwait does not see today. If a future org's template also has no Conclusions fields,
  // it will inherit this same all-types bypass -- that is the intended, template-driven
  // generalization of what was previously Kuwait-only behavior, not an accident.
  const hasConclusionsFields = templateHasConclusionsFields(report?.reportTemplate);

  const isPredictionGenerating = checkIsPredictionGenerating(type, report);
  const isPredictionGeneratedAlready = checkIsPredictionGeneratedAlready(type, report);

  const createReportTemplateSections = useCreateReportTemplateSectionsMutation({});

  const [showNotice, setShowNotice] = React.useState<boolean>(isPredictionGeneratedAlready || false);

  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as EvaluationSubRoute) || ("upload-files" as EvaluationSubRoute);
  const nextRoute = getNextRoute(currentRoute);

  const generationTypeLabel = GenerationTypeLabelMapping[type];

  const handleDialogClick = async () => {
    if (isPredictionGenerating) return setPromptDialogOpen(false);
    if (showNotice && [ENV_NAMES.DEV, ENV_NAMES.LOCAL].includes(APP_ENV)) return setShowNotice(false);

    try {
      await createReportTemplateSections.mutateAsync({
        input: { reportId: reportId || "<missing-report-id>", pageLocation: type, useConverter: true },
      });

      if (skipNavigate) {
        setPromptDialogOpen(false);
        reportQuery?.refetch();
      } else {
        go(`../${nextRoute}`);
      }
    } catch (err) {
      console.error(`Failed to kickoff ${generationTypeLabel} Generation`, err);
      notifyError(`Something went wrong kicking off ${generationTypeLabel} Generation`);
    }
  };

  useEffect(() => {
    if (promptDialogOpen && !hasConclusionsFields) {
      handleDialogClick();
    }
  }, [promptDialogOpen, hasConclusionsFields]);

  if (!hasConclusionsFields) return null;

  return (
    <Dialog
      size="xl"
      open={promptDialogOpen}
      setOpen={setPromptDialogOpen}
      title={showNotice || isPredictionGenerating ? "Notice" : `Confirm ${generationTypeLabel} Generation`}
      primaryButton={{
        text: getPrimaryButtonText({
          isPending: createReportTemplateSections.isPending,
          showNotice,
          isPredictionGenerating,
        }),
        buttonAction: showNotice ? "destructive" : "regular",
        onClick: handleDialogClick,
        disabled: createReportTemplateSections.isPending,
        className: "px-6",
      }}
      secondaryButton={
        !showNotice && isPredictionGenerating
          ? undefined
          : {
              text: showNotice ? "No, Cancel" : "Cancel",
              onClick: () => {
                if (showNotice) {
                  setPromptDialogOpen(false);
                } else {
                  setPromptDialogOpen(false);
                  if (isPredictionGeneratedAlready) setShowNotice(true);
                }
              },
              className: "px-6 hover:no-underline",
            }
      }
    >
      <ConfirmGenerationDialogBody
        showNotice={showNotice}
        isPredictionGenerating={isPredictionGenerating}
        generationTypeLabel={generationTypeLabel}
      />
    </Dialog>
  );
};
